import * as EventConstants from 'constants/EventConstants';
import {SECTIONS} from 'constants/NavConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import AppDispatcher from 'lib/Dispatcher';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import {expandSection} from 'nav/sidepanel/SidepanelActions';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import notifications from 'notifications';
import {Action} from 'tray/TrayTaskActions';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as net from 'util/Net';
import throttle from 'util/throttle';
import {updateWorklists} from 'worklists/WorklistActions';
import {WorklistFormType} from 'worklists/WorklistConstants';
import {openWorklistListing} from 'worklists/WorklistUIActions';

import {BulkAssemblyNewWorklistInfo, DataSourceType} from './BulkAssemblyConstants';
import * as BulkAssemblyDAO from './BulkAssemblyDAO';
import BulkAssemblyStore from './BulkAssemblyStore';
import {SlotParams} from './BulkAssemblyUtil';

const throttledValidate = throttle(async () => {
  const assembly = BulkAssemblyStore.getAssembly();
  try {
    const summary = await BulkAssemblyDAO.validate(assembly);

    AppDispatcher.dispatch({
      actionType: EventConstants.BULK_ASSEMBLY_VALIDATE_COMPLETED,
      assembly,
      summary,
    });
  } catch (e) {
    if (net.isServerRequestError(e)) {
      notifications.notifyError(e);
      AppDispatcher.dispatch({
        actionType: EventConstants.BULK_ASSEMBLY_VALIDATE_FAILED,
        assembly,
      });
    } else {
      throw e;
    }
  }
}, 2000);

export function validate(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_VALIDATE,
  });
  throttledValidate();
}

export function setTopology(topology: SequenceTopology): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_TOPOLOGY,
    topology,
  });
  validate();
}

export function addSlot(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_ADD_SLOT,
  });
  validate();
}

export function setSlotDataSource(slotIdx: number, dataSourceId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_SLOT_DATA_SOURCE,
    slotIdx,
    dataSourceId,
  });
  validate();
}

export function updateSlot(slotIdx: number, updates: Partial<SlotParams>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_UPDATE_SLOT,
    slotIdx,
    updates,
  });
  validate();
}

export function removeSlot(slotIdx: number): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_REMOVE_SLOT,
    slotIdx,
  });
  validate();
}

export function transitionToNextSlot(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_NEXT_SLOT,
  });
}

export function transitionToPreviousSlot(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_PREVIOUS_SLOT,
  });
}

export function updateDataSource(dataSourceId: string, updates: Partial<SlotParams>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_UPDATE_DATA_SOURCE,
    dataSourceId,
    updates,
  });
  validate();
}

export function activateStep(slotIdx: number): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_ACTIVATE_STEP,
    slotIdx,
  });
}

export function resetSlotDataSource(slotIdx: number, dataSourceType: DataSourceType): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_RESET_SLOT_DATA_SOURCE,
    slotIdx,
    dataSourceType,
  });
  validate();
}

export function setSequenceFolderId(folderId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SEQUENCE_FOLDER_ID,
    folderId,
  });
}

export function setPrimerFolderId(folderId: string): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_PRIMER_FOLDER_ID,
    folderId,
  });
}

export function setFragmentFolderId(folderId: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_FRAGMENT_FOLDER_ID,
    folderId,
  });
}

export function setTagSchemaId(tagSchemaId: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_TAG_SCHEMA_ID,
    tagSchemaId,
  });
  validate();
}

export function resetSchemaFields(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_RESET_SCHEMA_FIELDS,
  });
}

export function setUseSequenceWorklist(useWorklist: boolean): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_USE_SEQUENCE_WORKLIST,
    useWorklist,
  });
}

export function setUsePrimerWorklist(useWorklist: boolean): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_USE_PRIMER_WORKLIST,
    useWorklist,
  });
}

export function setUseFragmentWorklist(useWorklist: boolean): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_USE_FRAGMENT_WORKLIST,
    useWorklist,
  });
}

export function setSequenceWorklistFormType(newOrExisting: WorklistFormType): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_NEW_OR_EXISTING_SEQUENCE_WORKLIST,
    newOrExisting,
  });
}

export function setPrimerWorklistFormType(newOrExisting: WorklistFormType): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_NEW_OR_EXISTING_PRIMER_WORKLIST,
    newOrExisting,
  });
}

export function setFragmentWorklistFormType(newOrExisting: WorklistFormType): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_NEW_OR_EXISTING_FRAGMENT_WORKLIST,
    newOrExisting,
  });
}

export function setNewSequenceWorklistInfo(newWorklistInfo: BulkAssemblyNewWorklistInfo): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_NEW_SEQUENCE_WORKLIST_INFO,
    newWorklistInfo,
  });
}

export function setNewPrimerWorklistInfo(newWorklistInfo: BulkAssemblyNewWorklistInfo): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_NEW_PRIMER_WORKLIST_INFO,
    newWorklistInfo,
  });
}

export function setNewFragmentWorklistInfo(newWorklistInfo: BulkAssemblyNewWorklistInfo): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_NEW_FRAGMENT_WORKLIST_INFO,
    newWorklistInfo,
  });
}

export function setSequenceWorklistId(existingWorklistId: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_EXISTING_SEQUENCE_WORKLIST_ID,
    existingWorklistId,
  });
}

export function setPrimerWorklistId(existingWorklistId: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_EXISTING_PRIMER_WORKLIST_ID,
    existingWorklistId,
  });
}

export function setFragmentWorklistId(existingWorklistId: string | null): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_SET_EXISTING_FRAGMENT_WORKLIST_ID,
    existingWorklistId,
  });
}

export function reset(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_RESET,
  });
  validate();
}

export function getWorklistOrFolderAction(
  worklistSummary?: {id: string; itemType: string},
  folderId?: string | null
): Action {
  return {
    actionText: `Open ${worklistSummary ? 'worklist' : 'folder'}`,
    onActionClick: () => {
      FullScreenActions.hideScreen();
      if (worklistSummary) {
        openWorklistListing(worklistSummary);
        expandSection(SECTIONS.WORKLIST_ITEMS);
      } else {
        FileBrowserActions.openFile(folderId!);
      }
    },
  };
}

export async function runAssembly(): Promise<void> {
  const assembly = BulkAssemblyStore.getAssembly();
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ASSEMBLY_RUN,
  });

  try {
    LoadingScreen.show('Running assembly');
    const {worklistSummary, primerWorklistSummary} = await BulkAssemblyDAO.runAssembly(assembly);
    if (worklistSummary) {
      updateWorklists({[worklistSummary.id]: worklistSummary});
    }
    if (primerWorklistSummary) {
      updateWorklists({[primerWorklistSummary.id]: primerWorklistSummary});
    }

    LoadingScreen.hide();
    ToastActions.pushToast({
      type: ToastType.INFO,
      message: 'Bulk assembly completed.',
      duration: 0,
      actions: [
        getWorklistOrFolderAction(worklistSummary, assembly.sequenceSaveInformation.targetFolderId),
        {
          actionText: 'Create New Assembly',
          onActionClick: () => {
            AppDispatcher.dispatch({
              actionType: EventConstants.BULK_ASSEMBLY_RESET_SOURCES_AND_OUTPUT,
            });
          },
        },
      ],
      onClose(): void {
        AppDispatcher.dispatch({
          actionType: EventConstants.BULK_ASSEMBLY_RESET,
        });
      },
      skipCloseCallbackOnActions: true,
    });

    AppDispatcher.dispatch({
      actionType: EventConstants.BULK_ASSEMBLY_RUN_COMPLETED,
    });
  } catch (e) {
    LoadingScreen.hide();
    if (net.isServerRequestError(e)) {
      notifications.notifyError(e, {id: 'BulkAssemblyActions.runAssembly'});
      AppDispatcher.dispatch({
        actionType: EventConstants.BULK_ASSEMBLY_RUN_FAILED,
      });
    } else {
      throw e;
    }
  }
}
